import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { dashboardAPI } from '../services/api';
import MyWorkflows from './MyWorkflows';
import AIAgents from './AIAgents';
import Analytics from './Analytics';
import TeamSettings from './TeamSettings';

const Dashboard = () => {
  const [activeNav, setActiveNav] = useState('dashboard');
  const [isVisible, setIsVisible] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch dashboard stats from backend
  const { data: dashboardStats, loading: statsLoading, error: statsError, refetch } = useApi(
    dashboardAPI.getUserStats,
    []
  );

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
    { id: 'workflows', label: 'My Workflows', icon: 'account_tree' },
    { id: 'agents', label: 'AI Agents', icon: 'smart_toy' },
    { id: 'analytics', label: 'Analytics', icon: 'bar_chart' },
    { id: 'team', label: 'Team', icon: 'group' },
    { id: 'settings', label: 'Settings', icon: 'settings' }
  ];

  // Mock activity logs - in real app, this would come from backend
  const activityLogs = [
    {
      id: 1,
      agent: 'Support-Bot-01',
      action: 'Resolved high-priority ticket #442 via API-Webhook.',
      time: '2m ago',
      icon: 'support_agent',
      color: 'blue'
    },
    {
      id: 2,
      agent: 'Marketing-Flow',
      action: 'Initiated lead scoring for enterprise_list_v2. 450 profiles scanned.',
      time: '5m ago',
      icon: 'campaign',
      color: 'purple'
    },
    {
      id: 3,
      agent: 'Data-Sync-Agent',
      action: 'Database synchronization complete. 0 errors encountered.',
      time: '12m ago',
      icon: 'cloud_sync',
      color: 'orange'
    },
    {
      id: 4,
      agent: 'Security-Guard',
      action: 'Performed routine scan of OAuth credentials. All tokens valid.',
      time: '1h ago',
      icon: 'security',
      color: 'emerald'
    }
  ];

  return (
    <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-white min-h-screen relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-10 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-blue-500/3 rounded-full blur-2xl animate-float stagger-2"></div>
      
      <div className="flex h-screen overflow-hidden relative z-10">
        {/* Sidebar */}
        <aside className={`w-64 flex-shrink-0 border-r border-slate-200 dark:border-[#233c48] bg-white dark:bg-[#111c22] flex flex-col justify-between transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`}>
          <div className="flex flex-col">
            {/* Logo Section */}
            <div className="p-6 flex items-center gap-3 animate-fade-in-down stagger-1">
              <div className="bg-primary rounded-lg p-1 flex items-center justify-center hover-scale animate-heartbeat">
                <span className="material-symbols-outlined text-white text-2xl">orbit</span>
              </div>
              <div className="flex flex-col">
                <h1 className="text-white text-lg font-bold leading-none">OrbitMind</h1>
                <p className="text-[#92b7c9] text-xs font-medium mt-1 uppercase tracking-wider">Enterprise AI OS</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex flex-col gap-1 px-3 mt-4">
              {navItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-300 hover-lift animate-fade-in-left ${
                    activeNav === item.id
                      ? 'active-nav text-white shadow-lg'
                      : 'text-slate-400 hover:bg-[#233c48] hover:text-white hover:scale-105'
                  }`}
                  style={{animationDelay: `${index * 100}ms`}}
                  onClick={() => setActiveNav(item.id)}
                >
                  <span className={`material-symbols-outlined transition-transform ${activeNav === item.id ? 'animate-bounce-gentle' : ''}`}>{item.icon}</span>
                  <p className={`text-sm ${activeNav === item.id ? 'font-semibold' : 'font-medium'}`}>
                    {item.label}
                  </p>
                </div>
              ))}
            </nav>
          </div>

          {/* Footer Section */}
          <div className="p-4 border-t border-[#233c48] animate-fade-in-up stagger-6">
            <button 
              onClick={() => navigate('/pricing')}
              className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 hover:scale-105 hover:shadow-lg animate-glow"
            >
              <span className="material-symbols-outlined text-sm animate-bounce-gentle">upgrade</span>
              Upgrade Plan
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 flex flex-col overflow-hidden bg-background-light dark:bg-background-dark transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
          {/* Top Navigation */}
          <header className="h-16 border-b border-slate-200 dark:border-[#233c48] flex items-center justify-between px-8 bg-white/50 dark:bg-background-dark/50 backdrop-blur-md animate-fade-in-down stagger-2">
            <div className="flex items-center gap-6">
              {/* Quick Navigation to Public Pages */}
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => navigate('/')}
                  className="text-slate-400 hover:text-white text-sm font-medium transition-all flex items-center gap-1 hover:scale-105 animate-fade-in-right stagger-1"
                >
                  <span className="material-symbols-outlined text-sm animate-bounce-gentle">home</span>
                  Home
                </button>
                <button 
                  onClick={() => navigate('/services')}
                  className="text-slate-400 hover:text-white text-sm font-medium transition-all hover:scale-105 animate-fade-in-right stagger-2"
                >
                  Services
                </button>
                <button 
                  onClick={() => navigate('/pricing')}
                  className="text-slate-400 hover:text-white text-sm font-medium transition-all hover:scale-105 animate-fade-in-right stagger-3"
                >
                  Pricing
                </button>
                <button 
                  onClick={() => navigate('/about')}
                  className="text-slate-400 hover:text-white text-sm font-medium transition-all hover:scale-105 animate-fade-in-right stagger-4"
                >
                  About
                </button>
                <button 
                  onClick={() => navigate('/contact')}
                  className="text-slate-400 hover:text-white text-sm font-medium transition-all hover:scale-105 animate-fade-in-right stagger-5"
                >
                  Contact
                </button>
              </div>
              
              <div className="h-6 w-px bg-slate-200 dark:bg-[#233c48] animate-fade-in-up stagger-3"></div>
              
              <div className="relative w-64 animate-scale-in stagger-4">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl animate-pulse">search</span>
                <input 
                  className="w-full bg-slate-100 dark:bg-[#233c48] border-none rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-1 focus:ring-primary text-white placeholder:text-slate-500 transition-all focus:scale-105" 
                  placeholder="Command Search..." 
                  type="text"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-lg hover-scale animate-fade-in-left stagger-1">
                <span className="material-symbols-outlined text-primary text-sm mr-2 animate-bounce-gentle">database</span>
                <span className="text-primary text-xs font-bold">12,450 Credits</span>
              </div>
              <div className="h-8 w-px bg-slate-200 dark:border-[#233c48]"></div>
              <button className="text-slate-400 hover:text-white relative hover-scale animate-fade-in-up stagger-2">
                <span className="material-symbols-outlined">notifications</span>
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full border-2 border-background-dark animate-pulse"></span>
              </button>
              <button className="text-slate-400 hover:text-white hover-scale animate-fade-in-up stagger-3">
                <span className="material-symbols-outlined">chat_bubble</span>
              </button>
              <div className="flex items-center gap-3 ml-2 cursor-pointer hover:bg-[#233c48] p-1.5 rounded-lg transition-all group hover-scale animate-fade-in-up stagger-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-xs animate-heartbeat">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="text-sm font-medium dark:text-white hidden lg:block">
                  {user?.username || 'User'}
                </span>
                <div className="relative">
                  <span className="material-symbols-outlined text-slate-400 text-sm group-hover:text-white transition-all group-hover:rotate-180">expand_more</span>
                  {/* Dropdown menu could be added here */}
                  <div className="absolute right-0 top-8 hidden group-hover:block bg-[#111c22] border border-[#233c48] rounded-lg shadow-lg py-2 min-w-[120px] z-50 animate-scale-in">
                    <button 
                      onClick={logout}
                      className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-[#233c48] hover:text-white transition-all hover:scale-105"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-8 hide-scrollbar">
            {/* Render different content based on active navigation */}
            {activeNav === 'dashboard' && (
              <>
                {/* Page Heading */}
                <div className="mb-8 animate-fade-in-up stagger-1">
                  <h2 className="text-3xl font-black text-white tracking-tight">System Overview</h2>
                  <p className="text-[#92b7c9] mt-1">Real-time status of your enterprise AI infrastructure.</p>
                </div>

                {/* Grid Layout */}
                <div className="grid grid-cols-12 gap-6">
                  {/* Stats Section */}
                  <div className="col-span-12 lg:col-span-8 space-y-6">
                  {/* Top Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white dark:bg-[#111c22] border border-slate-200 dark:border-[#325567] rounded-xl p-6 relative overflow-hidden group hover-lift animate-fade-in-left stagger-2">
                      <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-2 bg-emerald-500/10 rounded-lg animate-bounce-gentle">
                            <span className="material-symbols-outlined text-emerald-500">automation</span>
                          </div>
                          {statsLoading ? (
                            <div className="w-16 h-4 bg-slate-600 animate-pulse rounded"></div>
                          ) : (
                            <span className="text-emerald-500 text-xs font-bold bg-emerald-500/10 px-2 py-1 rounded animate-fade-in-right">
                              {dashboardStats?.activeAgentsGrowth || '+12%'} vs last week
                            </span>
                          )}
                        </div>
                        <p className="text-[#92b7c9] text-sm font-medium">Active Automations</p>
                        <h3 className="text-white text-3xl font-bold mt-1">
                          {statsLoading ? (
                            <div className="w-20 h-8 bg-slate-600 animate-pulse rounded"></div>
                          ) : (
                            <>
                              {dashboardStats?.activeAgents || 24} 
                              <span className="text-lg text-slate-500 font-normal">
                                / {dashboardStats?.totalAgents || 30}
                              </span>
                            </>
                          )}
                        </h3>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-transparent opacity-30 group-hover:opacity-60 transition-opacity"></div>
                    </div>

                    <div className="bg-white dark:bg-[#111c22] border border-slate-200 dark:border-[#325567] rounded-xl p-6 relative overflow-hidden group hover-lift animate-fade-in-right stagger-3">
                      <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-2 bg-primary/10 rounded-lg animate-bounce-gentle stagger-1">
                            <span className="material-symbols-outlined text-primary">token</span>
                          </div>
                          {statsLoading ? (
                            <div className="w-16 h-4 bg-slate-600 animate-pulse rounded"></div>
                          ) : (
                            <span className="text-primary text-xs font-bold bg-primary/10 px-2 py-1 rounded animate-fade-in-left">On track</span>
                          )}
                        </div>
                        <p className="text-[#92b7c9] text-sm font-medium">Total Credits Used</p>
                        <h3 className="text-white text-3xl font-bold mt-1">
                          {statsLoading ? (
                            <div className="w-24 h-8 bg-slate-600 animate-pulse rounded"></div>
                          ) : (
                            dashboardStats?.creditsUsed?.toLocaleString() || '84,200'
                          )}
                        </h3>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-transparent opacity-30 group-hover:opacity-60 transition-opacity"></div>
                    </div>
                  </div>

                    {/* Live Agent Activity Feed */}
                    <div className="bg-white dark:bg-[#111c22] border border-slate-200 dark:border-[#233c48] rounded-xl overflow-hidden hover-lift animate-scale-in stagger-4">
                      <div className="p-5 border-b border-slate-200 dark:border-[#233c48] flex justify-between items-center">
                        <h4 className="text-white font-bold flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary animate-pulse">sensors</span>
                          Live Agent Activity
                        </h4>
                        <span className="text-xs text-slate-500 uppercase tracking-widest font-bold animate-blink">Real-time update</span>
                      </div>
                      <div className="p-0 max-h-[400px] overflow-y-auto">
                        <div className="divide-y divide-slate-100 dark:divide-[#233c48]">
                          {activityLogs.map((log, index) => (
                            <div key={log.id} className={`p-4 flex gap-4 hover:bg-slate-50 dark:hover:bg-[#1a2b35] transition-all hover-lift animate-fade-in-up`} style={{animationDelay: `${index * 200}ms`}}>
                              <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-${log.color}-500/10 flex items-center justify-center hover-scale`}>
                                <span className={`material-symbols-outlined text-${log.color}-500 text-xl animate-bounce-gentle`} style={{animationDelay: `${index * 300}ms`}}>{log.icon}</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between">
                                  <p className="text-sm font-bold text-white">{log.agent}</p>
                                  <span className="text-[10px] text-slate-500 animate-fade-in-left">{log.time}</span>
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">{log.action}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#111c22] border-t border-slate-200 dark:border-[#233c48] text-center">
                        <button className="text-xs font-bold text-primary hover:underline uppercase tracking-widest hover:scale-105 transition-all">View Full Log History</button>
                      </div>
                    </div>
                  </div>

                  {/* Side Panels */}
                  <div className="col-span-12 lg:col-span-4 space-y-6">
                    {/* Quick Actions Panel */}
                    <div className="bg-white dark:bg-[#111c22] border border-slate-200 dark:border-[#233c48] rounded-xl p-6">
                      <h4 className="text-white font-bold mb-5 flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary">bolt</span>
                        Quick Actions
                      </h4>
                      <div className="grid grid-cols-1 gap-3">
                        <button className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-[#325567] hover:border-primary/50 hover:bg-primary/5 transition-all text-left group">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                            <span className="material-symbols-outlined">add_task</span>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">Create Workflow</p>
                            <p className="text-[11px] text-slate-500">Build automated business flows</p>
                          </div>
                        </button>
                        <button className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-[#325567] hover:border-primary/50 hover:bg-primary/5 transition-all text-left group">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                            <span className="material-symbols-outlined">rocket_launch</span>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">Deploy Agent</p>
                            <p className="text-[11px] text-slate-500">Launch a new specialized AI agent</p>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* System Health */}
                    <div className="bg-gradient-to-br from-[#1a2b35] to-[#111c22] border border-[#233c48] rounded-xl p-6">
                      <h4 className="text-white font-bold mb-4">Infrastructure Health</h4>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-slate-400">Compute Load</span>
                            <span className="text-white font-bold">42%</span>
                          </div>
                          <div className="w-full bg-[#233c48] h-1.5 rounded-full overflow-hidden">
                            <div className="bg-primary h-full rounded-full" style={{width: '42%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-slate-400">Memory usage</span>
                            <span className="text-white font-bold">68%</span>
                          </div>
                          <div className="w-full bg-[#233c48] h-1.5 rounded-full overflow-hidden">
                            <div className="bg-primary h-full rounded-full" style={{width: '68%'}}></div>
                          </div>
                        </div>
                        <div className="pt-4 mt-4 border-t border-[#233c48] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                            <span className="text-[11px] text-emerald-500 font-bold uppercase tracking-wider">All Systems Operational</span>
                          </div>
                          <span className="material-symbols-outlined text-slate-500 text-sm">info</span>
                        </div>
                      </div>
                    </div>

                    {/* Organization Card */}
                    <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                          <img className="w-8 h-8 opacity-80" alt="Corporate logo" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBHhh53BNMkABAaFxiWvoh7HjktvaqKP3FOZF1CD3RcPSzanr0YQ5gUhhRVWHpe9V8lGyujpuEqEHhfMCco5yF-qnL6aoRT0pfNl-MvNAVom5i_ddul8jDuOYqBXbigSeve5rXR13ljuKzIUflpBL0xPkGnHvxd0mFQBxmUThvp6AobtyTzepPOjZoPcz0QgHyMJVrBNpH6IsGBydG2z5ieWRsIP7RhknKYS02CxJBat5H7mZnGxqgh1GGnVh6ZNTNGto3Ey1AA1w"/>
                        </div>
                        <div>
                          <h5 className="text-white font-bold text-sm">Global-Tech Inc.</h5>
                          <p className="text-xs text-slate-500">Enterprise Plan</p>
                        </div>
                      </div>
                      <button className="w-full bg-white/5 hover:bg-white/10 text-white text-xs font-bold py-2 rounded-lg transition-colors border border-white/10">
                        Switch Organization
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* My Workflows Page */}
            {activeNav === 'workflows' && <MyWorkflows />}

            {/* AI Agents Page */}
            {activeNav === 'agents' && <AIAgents />}

            {/* Analytics Page */}
            {activeNav === 'analytics' && <Analytics />}

            {/* Team Settings Page */}
            {activeNav === 'team' && <TeamSettings />}

            {/* Settings Page - Placeholder */}
            {activeNav === 'settings' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-black text-white tracking-tight">Settings</h2>
                  <p className="text-[#92b7c9] mt-1">Configure your system preferences and account settings.</p>
                </div>
                <div className="bg-white dark:bg-[#111c22] border border-slate-200 dark:border-[#233c48] rounded-xl p-8 text-center">
                  <span className="material-symbols-outlined text-slate-400 text-6xl mb-4">settings</span>
                  <h3 className="text-white font-bold text-xl mb-2">Settings Page</h3>
                  <p className="text-slate-400">System settings and preferences will be available here.</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;